import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as fromContactActions from '../actions/contact.actions';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { CrmService } from '../../crm/services/crm.service';
import { Contact } from '../../crm/models/contact.model';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NotificationService } from '@fc-core/services/notification.service';
import { Action, select, Store } from '@ngrx/store';
import { getContactList } from '../selectors';
import { HttpErrorResponse } from '@angular/common/http';
import { getErrorMessage } from '@fc-core/services/notification.utils';

@Injectable()
export class ContactEffects {
  constructor(
    private actions$: Actions,
    private crmService: CrmService,
    private dialog: MatDialog,
    private router: Router,
    private notifications: NotificationService,
    private store: Store,
  ) {}

  getContactList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.loadContacts),
      withLatestFrom(this.store.pipe(select(getContactList))),
      switchMap(([_, contactList]) => {
        if (contactList.length !== 0) {
          return [fromContactActions.cancelLoadContacts()];
        }
        return this.crmService.getContactList().pipe(
          map((data: { list: Contact[]; count: number }) => {
            const contacts = data.list.map((contact) => ({
              ...contact,
              // todo: before backend add fullName, we need to add it to the backend
              fullName: `${contact.firstName} ${contact.lastName}`,
            }));
            return fromContactActions.loadContactsSuccess({ contacts });
          }),
          catchError((err) => this.onRequestError(err)),
        );
      }),
    ),
  );

  getMoreContactList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.loadMoreContacts),
      switchMap(() => {
        return this.crmService.getContactList().pipe(
          map((data: { list: Contact[]; count: number }) =>
            fromContactActions.loadMoreContactsSuccess({
              contactList: data.list,
            }),
          ),
          catchError((err) => this.onRequestError(err)),
        );
      }),
    ),
  );

  updateContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.updateContact),
      switchMap((action) => {
        return this.crmService.updateContact(action.contact, action.id).pipe(
          map((contact: Contact) => {
            this.notifications.showSuccess('Contact successfully updated');
            this.dialog.closeAll();
            return fromContactActions.updateContactSuccess({
              contact: {
                id: action.id,
                changes: contact,
              },
            });
          }),
          catchError((err: HttpErrorResponse) =>
            this.onFormError(err, action.contact),
          ),
        );
      }),
    ),
  );

  addContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.addContact),
      switchMap((action) => {
        return this.crmService.addContact(action.contact).pipe(
          map((contact: Contact) => {
            if (!action.action) this.dialog.closeAll();
            this.notifications.showSuccess('Contact successfully added');
            if (action.action !== 'idle') this.navigateToContact(contact);
            return fromContactActions.addContactSuccess({
              contact,
            });
          }),
          catchError((err: HttpErrorResponse) =>
            this.onFormError(err, action.contact),
          ),
        );
      }),
    ),
  );

  updateContacts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.updateContacts),
      switchMap(({ contacts }) => {
        return this.crmService.updateContacts(contacts).pipe(
          map(() => {
            this.notifications.showSuccess('Contacts successfully updated');
            this.dialog.closeAll();
            return fromContactActions.updateContactsSuccess({ contacts });
          }),
          catchError((err) => {
            this.dialog.closeAll();
            return this.onRequestError(err);
          }),
        );
      }),
    ),
  );

  deleteContact$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromContactActions.deleteContact),
      switchMap((action) => {
        return this.crmService.deleteContact(action.id).pipe(
          map(() => {
            this.notifications.showSuccess('Contact successfully deleted');
            this.router.navigate(['/crm/main/contacts']);
            return fromContactActions.deleteContactSuccess({
              id: action.id,
            });
          }),
          catchError((err) => this.onRequestError(err)),
        );
      }),
    ),
  );

  private onRequestError(err: HttpErrorResponse): Observable<Action> {
    const errorMessage = getErrorMessage(err);
    this.notifications.showError(errorMessage);
    return of(fromContactActions.contactError({ err }));
  }

  private onFormError<T>(err: HttpErrorResponse, _item: T): Observable<Action> {
    const errorMessage = getErrorMessage(err);
    this.notifications.showError(errorMessage);
    return of(fromContactActions.contactError({ err }));
  }

  navigateToContact(contact: Contact) {
    this.router.navigate(['/crm/main/contacts/' + contact.id]);
  }
}
